import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { effect } from "@preact/signals-core";
import { themeSettings } from "../store/extended-offer-store";
import {
  applyPromoCode,
  validPromoCodes,
  isPromoCodeModalEnabled,
  initPromoCode,
} from "../store/promo-code-store";

// Global focus trap instance (loaded from CDN, see layout/theme.liquid)
declare const focusTrap: any;

const FADE_DURATION_MS = 300;

/**
 * Promo Code Modal — captures a merchant-defined promo code from the customer.
 *
 * On page load it applies any valid code from the `?promo_code=` URL param or an
 * existing cookie (silently). If no valid code is present and the feature is
 * enabled in theme settings, the modal opens so the customer can enter one.
 * Applying a code writes the `promo_code` cookie, which the cart store attaches
 * to line items as `_promo-code`.
 *
 * Presentational copy + background come from theme settings/locales, passed as
 * data-attributes by `snippets/promo-code-modal.liquid`.
 */
@customElement("promo-code-modal")
export class PromoCodeModal extends LitElement {
  @property({ type: String, attribute: "data-header" })
  headerCopy = "";

  @property({ type: String, attribute: "data-subheader" })
  subHeaderCopy = "";

  @property({ type: String, attribute: "data-placeholder" })
  placeholder = "";

  @property({ type: String, attribute: "data-submit-label" })
  submitLabel = "Submit";

  @property({ type: String, attribute: "data-no-code-label" })
  noCodeLabel = "";

  @property({ type: String, attribute: "data-no-code-copy" })
  noCodeCopy = "";

  @property({ type: String, attribute: "data-background-image" })
  backgroundImage = "";

  @state() private _isOpen = false;
  @state() private _isAnimatedIn = false;
  @state() private _isClosing = false;
  @state() private _value = "";
  @state() private _showError = false;
  @state() private _showNoCodeMessage = false;
  @state() private _suggestions: string[] = [];

  private _previouslyFocused: HTMLElement | null = null;
  private _focusTrap: any = null;
  private _closeTimeoutId: number | null = null;
  private _didInit = false;

  connectedCallback() {
    super.connectedCallback();

    // Theme settings (valid codes + enable flag) are populated asynchronously by
    // <theme-settings-provider>, so wait for them before deciding to prompt.
    // Apply any code already present (URL or cookie); only open the modal when
    // there is no valid code and the feature is enabled. Runs exactly once.
    effect(() => {
      if (this._didInit || themeSettings.value === null) return;
      this._didInit = true;
      const hasCode = initPromoCode();
      if (!hasCode && isPromoCodeModalEnabled.value) {
        this.open();
      }
    });

    this._focusTrap =
      typeof focusTrap !== "undefined"
        ? focusTrap.createFocusTrap(this, {
            initialFocus: () =>
              this.querySelector("#promo_code_input") ||
              this.querySelector(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
              ) ||
              this,
            escapeDeactivates: true,
            allowOutsideClick: true,
            onDeactivate: () => {
              if (
                this._previouslyFocused &&
                typeof this._previouslyFocused.focus === "function"
              ) {
                this._previouslyFocused.focus();
              }
            },
          })
        : null;

    this.addEventListener("keydown", this._handleKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("keydown", this._handleKeydown);
    if (this._closeTimeoutId) clearTimeout(this._closeTimeoutId);
    document.body.style.overflow = "";
  }

  updated(changedProperties: Map<string, unknown>) {
    super.updated?.(changedProperties);
    if (this._isOpen && !this._isClosing && !this._isAnimatedIn) {
      requestAnimationFrame(() => {
        this._isAnimatedIn = true;
      });
    }
  }

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  public open() {
    this._isOpen = true;
    this._isAnimatedIn = false;
    this._isClosing = false;
    this._value = "";
    this._showError = false;
    this._showNoCodeMessage = false;
    this._suggestions = [];
    document.body.style.overflow = "hidden";
    this._previouslyFocused = document.activeElement as HTMLElement;
    if (this._focusTrap) this._focusTrap.activate();
  }

  private _closeWithAnimation() {
    if (this._isClosing) return;
    this._isClosing = true;
    this._closeTimeoutId = window.setTimeout(() => {
      this._closeTimeoutId = null;
      this._isClosing = false;
      this._isAnimatedIn = false;
      this._close();
    }, FADE_DURATION_MS);
  }

  private _close() {
    this._isOpen = false;
    document.body.style.overflow = "";
    if (this._focusTrap) this._focusTrap.deactivate();
  }

  private _handleKeydown = (e: KeyboardEvent) => {
    if (e.key === "Escape" && this._isOpen) {
      e.stopPropagation();
      this._closeWithAnimation();
    }
  };

  private _handleBackdropClick = (e: Event) => {
    if (e.target === e.currentTarget) this._closeWithAnimation();
  };

  private _handleInput(e: Event) {
    const input = e.target as HTMLInputElement;
    this._value = input.value;
    this._showError = false;
    const query = input.value.trim().toUpperCase();
    this._suggestions = query
      ? validPromoCodes.value.filter((code) => code.startsWith(query))
      : [];
  }

  private _selectSuggestion(code: string) {
    this._value = code;
    this._suggestions = [];
    this._submit();
  }

  private _handleSubmit(e: Event) {
    e.preventDefault();
    this._submit();
  }

  private _submit() {
    if (applyPromoCode(this._value)) {
      this._suggestions = [];
      this._closeWithAnimation();
    } else {
      this._showError = true;
    }
  }

  private _toggleNoCode(e: Event) {
    e.preventDefault();
    this._showNoCodeMessage = !this._showNoCodeMessage;
  }

  render() {
    if (!this._isOpen) return html``;

    const isVisible = this._isAnimatedIn && !this._isClosing;
    const bgStyle = this.backgroundImage
      ? `background-image:url(${this.backgroundImage});background-size:cover;background-position:bottom center;`
      : "";

    return html`
      <div
        class="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4 transition-opacity duration-300 ${isVisible
          ? "opacity-100"
          : "opacity-0"}"
        role="dialog"
        aria-modal="true"
        aria-labelledby="promo-modal-heading"
        aria-hidden="${!isVisible}"
        @click=${this._handleBackdropClick}
      >
        <div
          class="relative w-full max-w-md overflow-hidden rounded-lg bg-white shadow-xl transition-all duration-300 ${isVisible
            ? "scale-100 opacity-100"
            : "scale-95 opacity-0"}"
          style="${bgStyle}"
          @click=${(e: Event) => e.stopPropagation()}
        >
          <div class="p-6 text-center">
            <button
              type="button"
              class="absolute right-3 top-3 p-1 text-current"
              aria-label="Close"
              @click=${() => this._closeWithAnimation()}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M1 1l14 14M15 1L1 15"
                  stroke="currentColor"
                  stroke-width="2"
                />
              </svg>
            </button>

            <h2 id="promo-modal-heading" class="h2 mb-2">${this.headerCopy}</h2>
            <p class="mb-4">${this.subHeaderCopy}</p>

            <form class="relative" @submit=${this._handleSubmit}>
              <label for="promo_code_input" class="sr-only"
                >${this.subHeaderCopy}</label
              >
              <input
                id="promo_code_input"
                class="w-full rounded-md border border-gray-300 px-3 py-2 uppercase"
                type="text"
                autocomplete="off"
                .value=${this._value}
                placeholder="${this.placeholder}"
                aria-invalid="${this._showError ? "true" : "false"}"
                @input=${this._handleInput}
                required
              />
              ${this._suggestions.length > 0
                ? html`
                    <ul
                      class="absolute z-10 mt-1 w-full overflow-hidden rounded-md border border-gray-200 bg-white text-left shadow"
                    >
                      ${this._suggestions.map(
                        (code) => html`
                          <li>
                            <button
                              type="button"
                              class="w-full px-3 py-2 text-left hover:bg-gray-100"
                              @click=${() => this._selectSuggestion(code)}
                            >
                              ${code}
                            </button>
                          </li>
                        `
                      )}
                    </ul>
                  `
                : ""}
              ${this._showError
                ? html`<p role="alert" class="mt-2 text-sm text-[var(--color-danger-800)]">
                    That code isn't valid.
                  </p>`
                : ""}
              <button
                type="submit"
                class="button button--full mt-3 w-full"
              >
                ${this.submitLabel}
              </button>
            </form>

            ${this.noCodeLabel
              ? html`
                  <p class="mt-4">
                    <a href="#" @click=${this._toggleNoCode}
                      >${this.noCodeLabel}</a
                    >
                  </p>
                  ${this._showNoCodeMessage
                    ? html`<p class="mt-2 text-sm">${this.noCodeCopy}</p>`
                    : ""}
                `
              : ""}
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface Window {
    promoCodeModal?: PromoCodeModal;
  }
}
